import React, { useState } from "react";
import {
    addFeatureIdeaComment,
    FeatureIdeaComment,
    formatRelativeTime,
} from "../services/featureRequestsService";

type Props = {
    ideaId: number;
    comments: FeatureIdeaComment[];
    readOnly?: boolean;
    onCommentAdded: (comment: FeatureIdeaComment) => void;
};

function Avatar({ url, name }: { url: string | null; name: string }) {
    if (url) {
        return <img src={url} alt="" className="feature-idea-avatar" />;
    }
    const initial = name?.trim()?.charAt(0)?.toUpperCase() || "?";
    return (
        <span className="feature-idea-avatar feature-idea-avatar--placeholder" aria-hidden="true">
            {initial}
        </span>
    );
}

export default function FeatureIdeaComments({ ideaId, comments, readOnly = false, onCommentAdded }: Props) {
    const [body, setBody] = useState("");
    const [error, setError] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmed = body.trim();
        if (!trimmed) {
            setError("Please enter a reply.");
            return;
        }
        if (trimmed.length > 1000) {
            setError("Reply must be 1,000 characters or fewer.");
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const created = await addFeatureIdeaComment(ideaId, trimmed);
            onCommentAdded(created);
            setBody("");
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "Failed to add comment.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="feature-idea-comments" data-testid={`feature-idea-comments-${ideaId}`}>
            {comments.map((comment) => (
                <div key={comment.id} className="feature-idea-comment">
                    <div className="feature-idea-comment-header">
                        <Avatar
                            url={comment.authorAvatarUrl}
                            name={comment.authorDisplayName}
                        />
                        <strong>{comment.authorDisplayName}</strong>
                        {comment.authorCompanyName && (
                            <>
                                <span className="feature-idea-meta-sep">|</span>
                                <span>{comment.authorCompanyName}</span>
                            </>
                        )}
                        <span className="feature-idea-meta-sep">·</span>
                        <span>{formatRelativeTime(comment.createdAt)}</span>
                    </div>
                    <p className="feature-idea-comment-body">{comment.body}</p>
                </div>
            ))}

            {!readOnly && (
                <form className="feature-idea-comment-form" onSubmit={handleSubmit}>
                    <textarea
                        value={body}
                        maxLength={1000}
                        placeholder="Write a reply…"
                        aria-label="Reply"
                        data-testid={`feature-idea-comment-input-${ideaId}`}
                        onChange={(e) => {
                            setBody(e.target.value);
                            if (error) setError(null);
                        }}
                    />
                    <button
                        type="submit"
                        className="feature-requests-pill-btn"
                        disabled={submitting}
                        data-testid={`feature-idea-comment-submit-${ideaId}`}
                    >
                        {submitting ? "…" : "Reply"}
                    </button>
                    {error && (
                        <div className="feature-idea-comment-error" role="alert">
                            {error}
                        </div>
                    )}
                </form>
            )}

            {comments.length === 0 && readOnly && (
                <p style={{ color: "#6b7280", margin: 0 }}>No comments yet.</p>
            )}
        </div>
    );
}
